import React, { useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import SkillEditorBody from './SkillEditorBody';
import { getExternalMcpList, type ExternalMcpItem } from '../features/claudeagents/claudeAgentApi';
import { extractSkillDescription } from '../utils/skillFrontmatter';

export type BundleItem =
  | { kind: 'skill'; name: string; content: string }
  | { kind: 'mcp'; name: string; url: string; needs_auth: boolean };

// Detail popup for a single skill or MCP chip inside an agent bundle preview
// (AgentDraftPreview / AgentBundlePreview), everywhere that preview appears -
// marketplace browse, Review Queue, My Creations, a resolved agent share.
//
// showSkillContent (default true) gates only a skill's full body (skill.md) -
// everything else (an MCP's real URL, needs_auth, description) always shows,
// there's no teaser/full-detail distinction for those. It defaults to true
// because reviewing a submission or looking at your own creation both need
// the real content to be useful; the one place that must pass false
// explicitly is the marketplace Browse detail page (MarketplaceToolDetail in
// MyToolsPage.tsx), where someone is deciding whether to install something
// they don't own yet — skill content is the sellable asset, and browse is
// exactly the context that shouldn't give it away up front.
//
// MCPs don't carry a description in the frozen snapshot (only name/url/
// needs_auth), so this looks it up from the external MCP catalog by URL,
// best-effort. Neither skills nor MCPs have a per-item icon anywhere in this
// app's data model — only a static per-type glyph is shown here, matching
// the icons already used for these types elsewhere.
const AgentBundleItemModal: React.FC<{ item: BundleItem; onClose: () => void; showSkillContent?: boolean }> = ({ item, onClose, showSkillContent = true }) => {
  const [mcpCatalogEntry, setMcpCatalogEntry] = useState<ExternalMcpItem | null>(null);
  const [mcpLoading, setMcpLoading] = useState(false);
  const skillEditorRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (item.kind !== 'mcp') return;
    setMcpLoading(true);
    getExternalMcpList()
      .then(list => setMcpCatalogEntry(list.find(m => m.url === item.url) ?? null))
      .catch(() => setMcpCatalogEntry(null))
      .finally(() => setMcpLoading(false));
  }, [item]);

  const skillDescription = item.kind === 'skill' ? extractSkillDescription(item.content) : null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(6px) saturate(1.2)', WebkitBackdropFilter: 'blur(6px) saturate(1.2)',
      zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--surface-card, #1a1a1f)',
        backdropFilter: 'blur(24px) saturate(1.3)', WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
        border: '1.5px solid var(--border-glass, rgba(255,255,255,0.1))', borderRadius: 18, padding: 24,
        width: '100%', maxWidth: showSkillContent ? 680 : 560, maxHeight: '80vh', overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 22, flexShrink: 0 }}>{item.kind === 'skill' ? '🧠' : '🖥️'}</div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', flex: 1, wordBreak: 'break-word' }}>{item.name}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)',
            display: 'flex', padding: 4, flexShrink: 0,
          }} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>

        {item.kind === 'skill' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div className="mt-detail-field-label">Description</div>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {skillDescription || 'No description available.'}
              </p>
            </div>
            {showSkillContent && (
              <div>
                <div className="mt-detail-field-label">Skill content</div>
                <SkillEditorBody
                  mode="preview"
                  onModeChange={() => {}}
                  body={item.content}
                  onBodyChange={() => {}}
                  textEditorRef={skillEditorRef}
                  insertMd={() => {}}
                  readOnly
                  hideModeTabs
                />
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div className="mt-detail-field-label">URL</div>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{item.url}</p>
            </div>
            <div>
              <div className="mt-detail-field-label">Credentials</div>
              <span className={`mt-badge ${item.needs_auth ? 'mt-badge--warning' : 'mt-badge--status'}`} style={{ marginTop: 4 }}>
                {item.needs_auth ? '🔑 Requires credentials to attach' : 'No credentials required'}
              </span>
            </div>
            <div>
              <div className="mt-detail-field-label">Description</div>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {mcpLoading ? 'Loading…' : mcpCatalogEntry?.description || 'No description available.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentBundleItemModal;
